import React from 'react';
import Icon from '../../../components/AppIcon';

const ResourceAllocationPanel = ({ tasks }) => {
  const calculateResourceUtilization = () => {
    const resourceMap = {};
    
    tasks?.forEach(task => {
      if (task?.assignedTo) {
        task?.assignedTo?.forEach(person => {
          if (!resourceMap?.[person?.name]) {
            resourceMap[person.name] = {
              name: person?.name,
              role: person?.role,
              totalHours: 0,
              tasks: []
            };
          }
          resourceMap[person.name].totalHours += task?.duration;
          resourceMap?.[person?.name]?.tasks?.push(task?.name);
        });
      }
    });

    return Object.values(resourceMap);
  };

  const resources = calculateResourceUtilization();

  const getUtilizationColor = (hours) => {
    if (hours > 160) return 'var(--color-error)';
    if (hours > 120) return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  const getUtilizationPercentage = (hours) => {
    const maxHours = 160;
    return Math.min((hours / maxHours) * 100, 100);
  };

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-foreground">Resource Allocation</h3>
        <p className="caption text-muted-foreground mt-1">
          Worker capacity and task assignments
        </p>
      </div>
      <div className="space-y-4">
        {resources?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Icon name="Users" size={48} color="var(--color-muted-foreground)" />
            <p className="mt-4 text-sm text-muted-foreground">No resources assigned</p>
          </div>
        ) : (
          resources?.map((resource, index) => (
            <div key={index} className="p-4 bg-muted rounded-lg">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                    {resource?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{resource?.name}</p>
                    <p className="caption text-muted-foreground">{resource?.role}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-foreground">{resource?.totalHours}h</p>
                  <p className="caption text-muted-foreground">Total Hours</p>
                </div>
              </div>

              <div className="mb-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="caption text-muted-foreground">Capacity Utilization</span>
                  <span className="caption font-medium" style={{ color: getUtilizationColor(resource?.totalHours) }}>
                    {Math.round(getUtilizationPercentage(resource?.totalHours))}%
                  </span>
                </div>
                <div className="w-full h-2 bg-background rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-250"
                    style={{
                      width: `${getUtilizationPercentage(resource?.totalHours)}%`,
                      backgroundColor: getUtilizationColor(resource?.totalHours)
                    }}
                  />
                </div>
              </div>

              <div>
                <p className="caption text-muted-foreground mb-2">Assigned Tasks ({resource?.tasks?.length})</p>
                <div className="flex flex-wrap gap-2">
                  {resource?.tasks?.slice(0, 3)?.map((taskName, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-background rounded text-xs text-foreground"
                    >
                      {taskName}
                    </span>
                  ))}
                  {resource?.tasks?.length > 3 && (
                    <span className="px-2 py-1 bg-background rounded text-xs text-muted-foreground">
                      +{resource?.tasks?.length - 3} more
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-3 bg-muted rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="Users" size={16} color="var(--color-primary)" />
            <span className="caption text-muted-foreground">Total Resources</span>
          </div>
          <p className="text-xl font-semibold text-foreground">{resources?.length}</p>
        </div>

        <div className="p-3 bg-muted rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="AlertTriangle" size={16} color="var(--color-error)" />
            <span className="caption text-muted-foreground">Over-allocated</span>
          </div>
          <p className="text-xl font-semibold text-foreground">
            {resources?.filter(r => r?.totalHours > 160)?.length}
          </p>
        </div>

        <div className="p-3 bg-muted rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="Clock" size={16} color="var(--color-success)" />
            <span className="caption text-muted-foreground">Avg Hours</span>
          </div>
          <p className="text-xl font-semibold text-foreground">
            {resources?.length > 0
              ? Math.round(resources?.reduce((sum, r) => sum + r?.totalHours, 0) / resources?.length)
              : 0}h
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResourceAllocationPanel;